import { Form, Steps } from "antd";
import Header from "../../../../../components/header/Header"
import { useCallback, useEffect, useMemo, useState } from "react";
import MemberDetailsForm from "./MemberDetailsForm";
import ClaimDetailsForm from "./ClaimDetailsForm";
import ClaimFormReview from "./ClaimFormReview";
import { useForm } from "antd/lib/form/Form";
import { useNavigate } from "react-router-dom";
import { RootStateOrAny, useSelector } from "react-redux";
import { errorNotification, successNotification } from "../../../../../components/UI/Toast/Toast";
import api from "../../../../../api/api";
import { errorHandler, formatBackendErrors, formatDatesInJSON } from "../../../../../utils/helper";

interface Step {
  id: number,
  name: string,
  key: string,
  component: React.ReactNode
}

type FieldType = 'Text' | 'Number' | 'Date' | 'Dropdown' | 'Checkbox';

interface DropdownOptions {
  label: string;
  value: string;
}

interface AdditionalDetails {
  date_format: string;
  description: string;
}

export interface DynamicClaimsApiFields {
  source_type: string;
  field_name: string;
  is_mandatory: boolean;
  field_type: FieldType;
  options: DropdownOptions[] | null;
  label: null | string;
  additional_details: AdditionalDetails | null;
}


const ClaimForm: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [ghiDetails, setGhiDetails] = useState<any>();
  const [dynamicFields, setDynamicFields] = useState<DynamicClaimsApiFields[]>([]);
  const { userDetails } = useSelector((store: RootStateOrAny) => store.data)
  const [form] = useForm();
  const navigate = useNavigate();
  const reformatPayload = (payload: any) => {
    return {
      ...payload,
      category: 'corporate',
      insurance_type: 'POLICY',
      product_type: 'INSURANCE',
      type: 'REIMBURSEMENT',
      // intimation_status: apiIntimation,
      ...payload?.insurer_payload && {
        insurer_payload: {
          ...formatDatesInJSON(payload?.insurer_payload, dynamicFields),
        }
      }
    };
  };
  const onNextPage = useCallback(() => {
    setActiveStep((prev: number) => prev + 1)
  }, [activeStep])
  const onPrevPage = useCallback(() => {
    setActiveStep((prev: number) => prev - 1)
  }, [activeStep])
  const onCancel = useCallback(() => {
    navigate('/claims')
  }, [])
  const onSave = useCallback(() => {
    form.validateFields()
      .then((val) => {
        setLoading(true)
        api.claims.createClaim(reformatPayload({
          ...val,
          policy_uuid: ghiDetails?.policy_uuid,
        })).then((res) => {
          setLoading(false)
          successNotification('Claim has been successfully raised')
          onCancel()
        }).catch((error) => {
          setLoading(false)
          errorHandler(error?.response?.data);
          errorNotification('Failed to add claim. Please check the form and try again');
          try {
            if (error?.response?.data?.error) {
              const formattedErrors = formatBackendErrors(error.response?.data?.error);
              form.setFields(formattedErrors);
            }
          } catch (e) {
            console.log(e);
          }
        })
      })
      .catch((error) => {

      })
  }, [form, ghiDetails?.policy_uuid])
  const steps: Step[] = useMemo(() => {
    return [
      {
        id: 0,
        name: 'Member Details',
        key: 'member',
        component: <MemberDetailsForm onNext={onNextPage} onCancel={onCancel} form={form} ghiDetails={ghiDetails} />
      },
      {
        id: 1,
        name: 'Claim Details',
        key: 'claim',
        component: <ClaimDetailsForm onNext={onNextPage} onPrev={onPrevPage} form={form} dynamicFields={dynamicFields} />
      },
      {
        id: 2,
        name: 'Review and Confirm',
        key: 'review',
        component: <ClaimFormReview onPrev={onPrevPage} onSave={onSave} form={form} dynamicFields={dynamicFields} ghiDetails={ghiDetails} loading={loading} />
      }
    ]
  }, [onNextPage, onPrevPage, onCancel, onSave, dynamicFields, ghiDetails, loading])
  const fetchInsurerFields = useCallback(() => {
    api.claims.getApiFields({ policy_uuid: ghiDetails?.policy_uuid })
      .then((res) => {
        setDynamicFields(res?.data)
      })
      .catch((error) => {
        errorHandler(error?.response?.data)
      })
  }, [ghiDetails?.policy_uuid]);

  useEffect(() => {
    if (userDetails?.policies && userDetails?.policies?.length > 0) {
      const ghi = userDetails?.policies?.find((item: any) => item?.provider_product_mapping?.product?.short_name === 'GHI')
      if (ghi) {
        setGhiDetails(ghi)
      } else {
        errorNotification('Claim can only be raised if you have Group Health Insurance Product')
        onCancel()
      }
    }
  }, [userDetails?.policies, onCancel])

  useEffect(() => {
    ghiDetails && fetchInsurerFields();
  }, [ghiDetails, fetchInsurerFields])

  return <div>
    <div className="show-on-web">
      <Header title="Claim Form" />
    </div>
    <div>
      <Steps current={activeStep} size="small" style={{
        maxWidth: '70%',
        margin: '48px auto'
      }}
        labelPlacement="vertical"
      >
        {steps?.map((item) => <Steps.Step key={item?.key} title={item?.name} />)}
      </Steps>
      <Form form={form}>
        {steps?.find((item: Step) => item?.id === activeStep)?.component}
      </Form>
    </div>
  </div>
};

export default ClaimForm;
